import { createElement as rc, Fragment } from 'react';
import Spinner from './Spinner';
import styled from '../styled';
import fromTheme from '../fromTheme';
import View from './View';
import nativeOnlyStyles from '../nativeOnlyStyles';

// Translucent scrim shown over the children while waiting.
//
// WaitScreen renders no box of its own, so this `position: absolute` resolves against the
// caller's nearest positioned ancestor: mount WaitScreen inside a `position: relative` element,
// or the scrim covers far more than intended. Use `modal.backgroundFade` (the translucent scrim
// shared with Modal and PortalHost), never `modal.backgroundColor` (the opaque modal surface).
let Loading = styled(View).attrs({ name: 'loading' })`
    position: absolute;
    z-index: ${({ theme }) => theme.zindex.PortalBackground};
    left: 0;
    top: 0;
    height: 100%;
    width: 100%;
    background-color: ${fromTheme('modal', 'backgroundFade')};
    justify-content: center;
    align-items: center;
`;
Loading = nativeOnlyStyles(Loading)`
    elevation: ${({ theme }) => theme.zindex.PortalBackground};
`;
Loading.displayName = 'Loading';

/**
 * Nest children inside to overlay them with a translucent loading screen which
 * prevents users from clicking stuff while waiting for the load to finish.
 *
 * Renders no wrapper element, so it never disturbs the caller's layout. See `Loading` above for
 * what that means for where the scrim lands.
 *
 * @param {Object} props
 * @param {boolean} props.waiting - set to true to overlay the children with a loading screen
 * @param {boolean} [props.showSpinner] - show the spinner while waiting. Defaults to true; an
 *   overlay with nothing in it is indistinguishable from the app having broken.
 * @returns function - react component
 */
export default function WaitScreen(props) {
    const { waiting, children, showSpinner = true } = props;
    // The structure is kept identical whether or not we are waiting. Returning bare children when
    // idle and a fragment when waiting reparents multi-child callers - which is all of them - and
    // remounts the subtree at the moment of submit.
    // prettier-ignore
    return rc(Fragment, null,
        children,
        waiting
            ? rc(Loading, {id: props.id ?? 'waitScreen'},
                showSpinner && rc(Spinner, { size: 'large' })
            )
            : null
    );
}
